import { toZonedTime } from 'date-fns-tz';

import { getFormattedTime } from '../../features/account/settings/settings-helpers';

type ScheduleWithOccurrence = {
  startTime: string | null;
  endTime?: string | null;
  daysOfWeek: number[];
};

export type NextScheduleOccurrence<T> = {
  schedule: T;
  startDay: number;
  endDay: number;
};

function getTimeInMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * From a list of schedules, return the one whose next scheduled occurrence is
 * soonest relative to the site's current local time.
 */
export function findNextScheduleOccurrence<T extends ScheduleWithOccurrence>(
  schedules: T[],
  timezone: string
): NextScheduleOccurrence<T> | null {
  const now = toZonedTime(new Date(), timezone);
  const currentDay = now.getDay();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  let nearest: NextScheduleOccurrence<T> | null = null;
  let nearestMinutesAway = Infinity;

  for (const schedule of schedules) {
    if (!schedule.startTime || !schedule.daysOfWeek.length) continue;

    const startMinutes = getTimeInMinutes(schedule.startTime);
    const endMinutes = schedule.endTime ? getTimeInMinutes(schedule.endTime) : null;

    for (const scheduledDay of schedule.daysOfWeek) {
      const dayOffset = (scheduledDay - currentDay + 7) % 7;
      let minutesAway = dayOffset * 24 * 60 + (startMinutes - nowMinutes);

      if (minutesAway < 0) {
        minutesAway += 7 * 24 * 60;
      }

      if (minutesAway < nearestMinutesAway) {
        nearestMinutesAway = minutesAway;
        nearest = {
          schedule,
          startDay: scheduledDay,
          endDay: endMinutes != null && endMinutes < startMinutes ? (scheduledDay + 1) % 7 : scheduledDay,
        };
      }
    }
  }

  return nearest;
}

export function findNextSchedule<T extends ScheduleWithOccurrence>(schedules: T[], timezone: string): T | null {
  return findNextScheduleOccurrence(schedules, timezone)?.schedule ?? null;
}

/**
 * Accepts a time string in either `"HH:MM"` / `"HH:MM:SS"` format or a full ISO 8601 datetime
 * string (e.g. `"2022-09-06T10:00:00+00:00"`), and returns the time formatted according to the
 * user's 12/24-hour preference (via `getFormattedTime`).
 *
 * @param time - The time string to format, or `null` / empty string.
 * @returns The formatted time string (e.g. `"10:00 AM"` or `"10:00"`), or `"N/A"` if falsy.
 */
export function formatScheduleTime(time: string | null): string {
  if (!time) return 'N/A';

  let datetime: Date;
  if (time.includes('T')) {
    // ISO datetime string (e.g. "2022-09-06T10:00:00+00:00") — parse directly
    datetime = new Date(time);
  } else {
    // "HH:MM" or "HH:MM:SS" schedule time string
    datetime = new Date();
    const [hours, mins] = time.split(':');
    datetime.setHours(Number(hours), Number(mins), 0);
  }
  return getFormattedTime(datetime);
}
